import { Person as PersonIcon, Lock as LockIcon } from '@mui/icons-material';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { useInboxActions } from '../hooks/Inbox/useInboxActions';
import { useAttend, useInboxLockStatus } from '../hooks';
import { useTicketFlowModals } from '../context';
import { BaseTicketResponse } from '../models';
import { GlobalButton } from '@/components';
import CallButton from './CallButton';

interface InboxRowActionsProps {
  rowData: Record<string, any>;
}

export const InboxRowActions = ({ rowData }: InboxRowActionsProps) => {
  const { setTicketData, openAttendModal } = useTicketFlowModals();

  const { onAttend, isAttendCustomerLoading } = useAttend();
  const { onToggleInboxIsTaken, isToggleInboxIsTakenLoading } =
    useInboxActions();
  const { getLockStatus, currentUserId, fullName } = useInboxLockStatus();

  const { id, status, sectorId, trackNumber, attentionUserName, takenByName } =
    rowData;
  const { isTakenByOther, isTakenByMe, isManuallyTakenByMe } =
    getLockStatus(rowData);

  const handleAttendCustomer = async () => {
    if (isTakenByOther) return;

    if (status === 'PENDING') {
      const totemSectorBody = {
        sectorId,
        trackNumber,
      };
      const success = await onAttend({
        inboxId: id,
        totemSectorBody,
        isAlreadyTaken: isManuallyTakenByMe,
      });
      if (!success) return;
    }

    const preparedTicketData: BaseTicketResponse = {
      inboxId: id,
      trackNumber,
      sectorId,
      isAlreadyTaken: isManuallyTakenByMe,
    };

    setTicketData(preparedTicketData);
    openAttendModal();
  };

  const handleToggleLock = async () => {
    await onToggleInboxIsTaken({
      inboxId: id,
      takenById: currentUserId,
      takenByName: fullName,
    });
  };

  const handleBeforeCall = async (): Promise<boolean> => {
    if (isTakenByMe) return true;
    return (await onToggleInboxIsTaken({
      inboxId: id,
      takenById: currentUserId,
      takenByName: fullName,
    })) ?? false;
  };

  const getButtonText = () => {
    if (isTakenByOther) {
      if (status === 'ATTENDING') {
        return ` Atendiendo por ${attentionUserName || 'otro'}`;
      }
      return ` Tomado por ${takenByName || 'otro'}`;
    }
    if (status === 'PENDING') {
      return ' Atender';
    }
    if (attentionUserName) {
      return ` Atendiendo: ${attentionUserName}`;
    }
    return ' Continuar atendiendo';
  };

  const getToggleText = () => {
    if (isTakenByMe) return ' Liberar';
    return ' Tomar';
  };

  return (
    <div className='flex flex-col gap-2'>
      {!isTakenByOther && (
        <GlobalButton
          variant={
            isTakenByMe
              ? GlobalButtonVariant.secondaryButton
              : GlobalButtonVariant.primaryButton
          }
          disabled={isToggleInboxIsTakenLoading}
          action={handleToggleLock}
        >
          {isTakenByMe ? <LockIcon /> : <LockIcon />}
          {getToggleText()}
        </GlobalButton>
      )}

      <GlobalButton
        variant={GlobalButtonVariant.primaryButton}
        disabled={isAttendCustomerLoading || isTakenByOther}
        action={handleAttendCustomer}
      >
        {isTakenByOther ? <LockIcon /> : <PersonIcon />}
        {getButtonText()}
      </GlobalButton>

      {!isTakenByOther && (
        <CallButton
          sectorId={sectorId}
          trackNumber={trackNumber}
          buttonVariant={GlobalButtonVariant.primaryButton}
          onBeforeCall={handleBeforeCall}
        />
      )}
    </div>
  );
};
